import type {
  ContentObject,
  ContentTypeDefinition,
} from '../../../lib/flotiq-client/types/api-response';
import type { StatusPillColors } from '../../StatusPill/StatusPill';

import { GridRenderFieldEvent } from '../../../lib/flotiq-plugins/plugin-events/GridRenderFieldEvent';

import usePluginResults from '../../../hooks/usePluginResults';

import ElementFromPlugin from '../../ElementFromPlugin/ElementFromPlugin';
import {
  checkboxRenderer,
  dateRenderer,
  defaultRenderer,
  geoRenderer,
  objectRenderer,
  pillRenderer,
  relationRenderer,
  webhookActionsRenderer,
} from './cellRenderer';

export const INPUT_TYPES_TO_RENDER = [
  'checkbox',
  'geo',
  'radio',
  'select',
  'dateTime',
  'datePublished',
  'datasource',
  'text',
  'email',
  'number',
  'object',
  'webhookActions',
  'simpleList',
];

interface DataGridCellProps {
  /**
   * Input type to render
   */
  inputType?: string;
  /**
   * Data to render
   */
  data?: any;
  /**
   * Items from properties config for cell data
   */
  items?: {
    order: string[];
    propertiesConfig: {
      [key: string]: { inputType: string } & Record<string, any>;
    };
  };
  /**
   * Pill color for select and radio input types
   */
  color?: string;
  /**
   * Cell content type name
   */
  contentTypeName?: string;
  /**
   * Cell accessor
   */
  accessor?: string;
  /**
   * Cell content object data
   */
  contentObject?: ContentObject;
  /**
   * Cell content type definition
   */
  contentType?: ContentTypeDefinition;
  /**
   * Test ID for the cell
   */
  testId?: string;
}

/**
 * @emits FlotiqPlugins."flotiq.grid.cell::render"
 */
const DataGridCell = ({
  inputType = '',
  data = '',
  items,
  color,
  contentTypeName = '',
  accessor = '',
  contentObject,
  contentType,
  testId,
}: DataGridCellProps) => {
  /**
   * @emits FlotiqPlugins."flotiq.grid.cell::render"
   */
  const results = usePluginResults(
    'flotiq.grid.cell::render',
    GridRenderFieldEvent,
    {
      inputType,
      accessor,
      data,
      contentTypeName,
      contentObject,
      contentType,
    },
  );

  if (results && results.length > 0) {
    return <ElementFromPlugin results={results} />;
  }

  switch (inputType) {
    case 'checkbox': {
      return checkboxRenderer(data, testId);
    }
    case 'geo': {
      return geoRenderer(data);
    }
    case 'radio':
    case 'simpleList':
    case 'select': {
      return pillRenderer(data, color as StatusPillColors);
    }
    case 'webhookActions': {
      return webhookActionsRenderer(data, color as StatusPillColors, testId);
    }
    case 'dateTime': {
      return dateRenderer(data);
    }
    case 'datePublished': {
      return dateRenderer(data);
    }
    case 'datasource':
      return relationRenderer(data, testId);
    case 'object': {
      if (!items) return;
      return objectRenderer(data, items);
    }
    case 'text':
    case 'email':
    case 'number':
    default:
      return defaultRenderer(data);
  }
};

export default DataGridCell;
